import { isPendingSignup } from '@/components/signup/activation';
import * as React from 'react';
import {
	Check,
	ChevronsUpDown,
	Loader2,
	MapPin,
	MessageCircle,
	Plus,
	Search,
} from 'lucide-react';
import { BottomSheet, BottomSheetTextButton } from '@/components/mobile';

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar';
import { useSelector } from 'react-redux';
import { switchUser } from '@/actions';
import { useDispatch } from 'react-redux';
import AddNewLocationDialog from '../dialog/AddNewLocationDialog';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import formatPhoneForUser from '@/utils/formatPhoneForUser';

const CAN_ADD_LOCATION = ['owner', 'location-admin', 'team-leader'];

export function AccountSwitcher() {
	const { isMobile, open } = useSidebar();
	const location = useLocation();

	const businessGroup = useSelector((state: any) => state.businessGroup);
	const auth = useSelector((state: any) => state.auth);

	const { reseller, user } = auth;

	const dispatch = useDispatch();

	const [showAddNewLocationDialog, setShowAddNewLocationDialog] =
		React.useState(false);
	const [openAccountSwitcher, setOpenAccountSwitcher] = React.useState(false);
	const [filterText, setFilterText] = React.useState('');
	const activeTeam = React.useMemo<{
		_id?: string;
		name?: string;
		address?: string;
		twilioNumber?: string;
	} | null>(() => {
		if (!businessGroup?.length) return null;
		if (user.isEnterpriseEnabled) return { name: 'All locations' };
		return (
			businessGroup.find((business: any) => business._id === auth._id) ??
			null
		);
	}, [auth._id, businessGroup, user.isEnterpriseEnabled]);
	const activeTeamName = activeTeam?.name || 'Select location';

	const activeTeamInitials = React.useMemo(() => {
		const words = (activeTeam?.name || '')
			.trim()
			.split(/\s+/)
			.filter(Boolean);
		if (words.length === 0) return '';
		if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
		return (words[0][0] + words[words.length - 1][0]).toUpperCase();
	}, [activeTeam?.name]);

	// Sorted by name and narrowed by the filter; shared by the dropdown and the phone sheet.
	const visibleLocations = React.useMemo(() => {
		const searchText = filterText.toLowerCase();
		return [...(businessGroup ?? [])]
			.sort((a, b) => {
				const nameA = (a.name || '').toString();
				const nameB = (b.name || '').toString();
				return nameA.localeCompare(nameB);
			})
			.filter((business: any) => {
				if (!filterText) return true;
				const name = (business.name || '').toLowerCase();
				const address = (business.address || '').toLowerCase();
				return name.includes(searchText) || address.includes(searchText);
			});
	}, [businessGroup, filterText]);

	const canAddLocation =
		!reseller && !isPendingSignup(auth) && CAN_ADD_LOCATION.includes(user.accessLevel);

	// Switching reloads the dashboard, so the phone sheet shows which row is loading.
	const [switchingTo, setSwitchingTo] = React.useState<string | null>(null);

	const hasOtherUnread = React.useMemo(
		() =>
			businessGroup?.some(
				(b: any) => b._id !== activeTeam?._id && b.hasUnreadMessages,
			) ?? false,
		[businessGroup, activeTeam],
	);

	const handleSwitchLocation = (
		_selectedBusiness: string | { _id?: string },
		goToRootUrl = false,
	) => {
		const businessId =
			typeof _selectedBusiness === 'string'
				? _selectedBusiness
				: _selectedBusiness?._id;

		if (!businessId) {
			return;
		}

		const { pathname } = location;

		// get base path and disregard other parameters when switching location
		const basePath = pathname.split('/').slice(0, 3).join('/');

		return dispatch(
			switchUser({
				_business: businessId,
				goToRootUrl,
				goToBasePath: basePath,
			}) as any,
		);
	};

	const handleCloseNewLocationDialog = () => {
		setOpenAccountSwitcher(false);
		setShowAddNewLocationDialog(false);
	};

	const changeLocation = (val: any) => {
		if (val === 'open_dialog') {
			setOpenAccountSwitcher(false);
			setShowAddNewLocationDialog(true);
			return;
		}

		if (activeTeam?._id === val._id) {
			setOpenAccountSwitcher(false);
			return;
		}

		// handles the case where user is switching to ALL LOCATIONS which is a string
		if (val._id) {
			handleSwitchLocation(val._id);
		} else {
			handleSwitchLocation(val);
		}

		setOpenAccountSwitcher(false);
	};

	const choosePhoneLocation = (val: any) => {
		const isAllLocations = val === 'ALL-LOCATIONS';
		if (activeTeam?._id === (isAllLocations ? undefined : val._id)) {
			setOpenAccountSwitcher(false);
			return;
		}
		setSwitchingTo(isAllLocations ? val : val._id);
		// A successful switch reloads the page and resolves with nothing; a
		// failed one resolves with the error toast's action. Let the sheet go
		// again then, so the person can retry or close it.
		Promise.resolve(handleSwitchLocation(isAllLocations ? val : val._id)).then(
			(failure) => {
				if (failure) setSwitchingTo(null);
			},
			() => setSwitchingTo(null),
		);
	};

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				{/* Phones get a bottom sheet from the same button. */}
				<DropdownMenu
					open={openAccountSwitcher && !isMobile}
					onOpenChange={(open) => {
						setOpenAccountSwitcher(open);
						if (!open) setFilterText('');
					}}
				>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							tooltip={!open ? activeTeamName : undefined}
							className={cn(
								'h-auto py-0 items-center data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
								open
									? 'gap-3'
									: 'flex-col justify-center gap-0.5 group-data-[collapsible=icon]:!size-auto group-data-[collapsible=icon]:!h-auto group-data-[collapsible=icon]:!w-full group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:!py-1',
							)}
						>
							{open ? (
								<div className="flex flex-1 flex-col text-left min-w-0">
									<span className="flex items-center gap-1.5 text-sm font-semibold text-sidebar-foreground">
										<span className="truncate">
											{activeTeamName}
										</span>
										{hasOtherUnread && (
											<span className="size-2 shrink-0 rounded-full bg-blue-500" />
										)}
									</span>
									<div className="mt-1 flex flex-col gap-1 text-xs">
										{activeTeam?.address && (
											<span className="flex items-center gap-1 truncate text-muted-foreground">
												<MapPin className="size-3 shrink-0 opacity-70" />
												<span className="truncate">
													{activeTeam.address}
												</span>
											</span>
										)}
										{activeTeam?.twilioNumber && (
											<span className="flex items-center gap-1 truncate text-muted-foreground">
												<MessageCircle className="size-3 shrink-0 opacity-70" />
												<span className="truncate">
													{formatPhoneForUser(
														activeTeam.twilioNumber,
													)}
												</span>
											</span>
										)}
										{user.isEnterpriseEnabled && (
											<span className="text-xs text-muted-foreground -mt-1">
												{businessGroup.length} locations
											</span>
										)}
									</div>
								</div>
							) : (
								<div className="relative flex w-full flex-col items-center gap-0.5">
									<span className="flex size-7 items-center justify-center rounded-md bg-sidebar-accent text-[11px] font-semibold uppercase text-sidebar-foreground">
										{activeTeamInitials}
									</span>
									<ChevronsUpDown className="size-3 shrink-0 text-muted-foreground" />
									{hasOtherUnread && (
										<span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-blue-500" />
									)}
								</div>
							)}
							{open && (
								<ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
							)}
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg "
						align="start"
						side={isMobile ? 'bottom' : 'right'}
						sideOffset={4}
					>
						<div className="p-2">
							<Input
								type="text"
								placeholder="Filter locations..."
								value={filterText}
								onChange={(e) => setFilterText(e.target.value)}
								className="h-8 text-xs"
							/>
						</div>
						{businessGroup && businessGroup.length > 1 && (
							<>
								<DropdownMenuSeparator className="mx-2" />
								<DropdownMenuItem
									key={'ALL-LOCATIONS'}
									onClick={() =>
										changeLocation('ALL-LOCATIONS')
									}
									className={cn(
										activeTeam?.name === 'All Locations'
											? 'bg-accent py-2'
											: 'py-2',
									)}
								>
									<div className="flex items-center gap-3 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 [&_svg]:text-primary ">
										All Locations
										<span className="text-xs text-muted-foreground">
											({businessGroup.length})
										</span>
									</div>
								</DropdownMenuItem>
								<DropdownMenuSeparator className="mx-2" />
							</>
						)}
						<div className="overflow-y-auto overflow-x-hidden max-h-[80dvh]">
							{visibleLocations.map((business: any) => (
									<DropdownMenuItem
										key={business._id}
										onClick={() => changeLocation(business)}
										className={cn(
											activeTeam?.name === business.name
												? 'bg-accent'
												: '',
											'gap-2 p-2',
										)}
									>
										<div className="flex w-full items-start gap-3">
											<div className="flex flex-1 flex-col gap-1 truncate">
												<p
													className={cn(
														'truncate text-sm font-medium text-foreground',
														business.hasUnreadMessages &&
															'font-semibold',
													)}
												>
													{business.name}
												</p>
												<div className="space-y-1 text-xs text-muted-foreground">
													{business?.address && (
														<div className="flex items-center gap-1 truncate">
															<MapPin className="size-3 shrink-0 opacity-70" />
															<span className="truncate">
																{
																	business.address
																}
															</span>
														</div>
													)}
													{business?.twilioNumber && (
														<div className="flex items-center gap-1 truncate">
															<MessageCircle className="size-3 shrink-0 opacity-70" />
															<span className="truncate">
																{formatPhoneForUser(
																	business.twilioNumber,
																)}
															</span>
														</div>
													)}
												</div>
											</div>
											{business.hasUnreadMessages && (
												<div className="relative mt-1 flex size-3 shrink-0 items-center justify-center">
													<div className="size-2 rounded-full bg-blue-500 animate-pulse" />
													<div className="absolute size-2 rounded-full bg-blue-500 opacity-50 animate-ping" />
												</div>
											)}
										</div>
									</DropdownMenuItem>
								))}
						</div>
						<DropdownMenuSeparator className="mx-2" />
						{canAddLocation && (
								<DropdownMenuItem
									className="gap-2 p-2"
									onClick={() =>
										changeLocation('open_dialog')
									}
								>
									<div className="flex size-6 items-center justify-center rounded-md border bg-background">
										<Plus className="size-4" />
									</div>
									<div className="font-medium">
										Add location
									</div>
								</DropdownMenuItem>
							)}
					</DropdownMenuContent>
				</DropdownMenu>
				{isMobile && (
					<BottomSheet
						open={openAccountSwitcher}
						onOpenChange={(open) => {
							if (switchingTo) return;
							setOpenAccountSwitcher(open);
							if (!open) setFilterText('');
						}}
						title="Locations"
						size={businessGroup?.length > 6 ? 'large' : 'auto'}
						headerLeft={<span />}
						headerRight={
							<BottomSheetTextButton
								strong
								onClick={() => setOpenAccountSwitcher(false)}
								disabled={!!switchingTo}
							>
								Done
							</BottomSheetTextButton>
						}
						bodyClassName="px-0"
					>
						{businessGroup?.length > 6 && (
							<label className="mx-4 mb-2 flex h-10 items-center gap-2 rounded-[10px] bg-muted px-3">
								<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
								<input
									type="search"
									value={filterText}
									onChange={(e) => setFilterText(e.target.value)}
									placeholder="Search locations"
									aria-label="Search locations"
									autoCapitalize="none"
									autoCorrect="off"
									autoComplete="off"
									enterKeyHint="search"
									className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
								/>
							</label>
						)}
						<div role="list" aria-label="Locations">
							{businessGroup?.length > 1 && !filterText && (
								<PhoneLocationRow
									title="All Locations"
									subtitle={`${businessGroup.length} locations`}
									selected={!!user.isEnterpriseEnabled}
									switching={switchingTo === 'ALL-LOCATIONS'}
									disabled={!!switchingTo}
									onSelect={() => choosePhoneLocation('ALL-LOCATIONS')}
								/>
							)}
							{visibleLocations.map((business: any) => (
								<PhoneLocationRow
									key={business._id}
									title={business.name}
									address={business.address}
									phone={
										business.twilioNumber
											? formatPhoneForUser(business.twilioNumber)
											: undefined
									}
									unread={business.hasUnreadMessages}
									selected={!user.isEnterpriseEnabled && business._id === auth._id}
									switching={switchingTo === business._id}
									disabled={!!switchingTo}
									onSelect={() => choosePhoneLocation(business)}
								/>
							))}
							{filterText && visibleLocations.length === 0 && (
								<p className="px-4 py-6 text-center text-[15px] text-muted-foreground">
									No matching locations
								</p>
							)}
						</div>
						{canAddLocation && (
							<button
								type="button"
								disabled={!!switchingTo}
								onClick={() => changeLocation('open_dialog')}
								className="mt-2 flex min-h-12 w-full items-center gap-3 px-4 text-left text-[17px] text-primary active:bg-muted disabled:opacity-40"
							>
								<Plus className="size-5" aria-hidden />
								Add location
							</button>
						)}
					</BottomSheet>
				)}
			</SidebarMenuItem>
			<AddNewLocationDialog
				handleClose={handleCloseNewLocationDialog}
				isOpen={showAddNewLocationDialog}
				handleSwitchLocation={handleSwitchLocation}
			/>
		</SidebarMenu>
	);
}

function PhoneLocationRow({
	title,
	subtitle,
	address,
	phone,
	unread,
	selected,
	switching,
	disabled,
	onSelect,
}: {
	title: string;
	subtitle?: string;
	address?: string;
	phone?: string;
	unread?: boolean;
	selected: boolean;
	switching: boolean;
	disabled: boolean;
	onSelect: () => void;
}) {
	return (
		<div role="listitem">
			<button
				type="button"
				onClick={onSelect}
				disabled={disabled}
				aria-current={selected ? 'true' : undefined}
				className="flex min-h-14 w-full items-center gap-3 pl-4 text-left active:bg-muted disabled:opacity-100"
			>
				<span className="flex min-w-0 flex-1 items-center gap-3 border-b border-border/60 py-2.5 pr-4">
					<span className="min-w-0 flex-1">
						<span className={cn('block truncate text-[16px] leading-snug', unread ? 'font-semibold' : 'font-medium')}>
							{title}
						</span>
						{subtitle && <span className="block truncate text-[13px] text-muted-foreground">{subtitle}</span>}
						{address && (
							<span className="flex items-center gap-1 truncate text-[13px] text-muted-foreground">
								<MapPin className="size-3 shrink-0 opacity-70" aria-hidden />
								<span className="truncate">{address}</span>
							</span>
						)}
						{phone && (
							<span className="flex items-center gap-1 truncate text-[13px] text-muted-foreground">
								<MessageCircle className="size-3 shrink-0 opacity-70" aria-hidden />
								<span className="truncate">{phone}</span>
							</span>
						)}
					</span>
					{unread && (
						<span className="size-2.5 shrink-0 rounded-full bg-blue-500">
							<span className="sr-only">Unread messages</span>
						</span>
					)}
					{switching ? (
						<Loader2 className="size-5 shrink-0 animate-spin text-muted-foreground" aria-label="Switching" />
					) : (
						selected && <Check className="size-5 shrink-0 text-primary" strokeWidth={2.5} aria-label="Current location" />
					)}
				</span>
			</button>
		</div>
	);
}
